import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import FormDialog from "common/Modal/Modal";
import AlertMessages from "common/Alert/AlertMessages";
import BackendPaginationTable from "common/Table/TableWithPagination";
import Loader from "layout/Loader";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import {
  RackProfileService,
  StoreProfileService,
} from "subModule/src/core/services";
import {
  BinResponse,
  RackResponse,
  ShelfResponse,
} from "subModule/src/services/rack/interface";
import {
  getRackCode,
  getRackTitle,
  normalizeRacksList,
  normalizeShelvesFromRack,
} from "subModule/src/services/rack/service";
import { countRackBins, fullBinAddress, isBinInventoryMode } from "./rackUtils";
import { ReactComponent as StoreIcon } from "assets/SideBarIcons/store.svg";
import StoreTabs from "../StoreTabs";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { CheckRTL } from "Helpers/CheckRTL";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { useFrozenStoreTabBreadcrumbs } from "Helpers/useFrozenStationBreadcrumbs";

type SimpleFormState = { title: string; code: string };

type NestedSectionProps = {
  accent: string;
  icon: ReactNode;
  title: string;
  breadcrumb: string[];
  countLabel: string;
  emptyTitle: string;
  emptyHint: string;
  isEmpty: boolean;
  actionLabel?: string;
  onAction?: () => void;
  children?: ReactNode;
};

function NestedSection({
  accent,
  icon,
  title,
  breadcrumb,
  countLabel,
  emptyTitle,
  emptyHint,
  isEmpty,
  actionLabel,
  onAction,
  children,
}: NestedSectionProps) {
  const isRTL = CheckRTL();
  return (
    <Box
      dir={isRTL ? "rtl" : "ltr"}
      sx={{
        m: 1.5,
        marginInlineStart: 2,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: `${accent}33`,
        borderInlineStart: `4px solid ${accent}`,
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          px: 2,
          py: 1,
          backgroundColor: `${accent}0F`,
          borderBottom: isEmpty ? "none" : `1px solid ${accent}22`,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
              mb: 0.75,
            }}
          >
            {breadcrumb.map((crumb, index) => (
              <Box
                key={`${crumb}-${index}`}
                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
              >
                {/* {index > 0 ? (
                  <Typography
                    component="span"
                    sx={{
                      color: "#9AA5B5",
                      fontSize: 11,
                      fontFamily: "Saira, sans-serif",
                    }}
                  >
                    ›
                  </Typography>
                ) : null} */}
                {/* <Chip
                  size="small"
                  label={crumb}
                  sx={{
                    height: 22,
                    fontFamily: "Saira, sans-serif",
                    fontWeight: 600,
                    fontSize: 10,
                    backgroundColor:
                      index === breadcrumb.length - 1
                        ? `${accent}1A`
                        : "#EEF2F6",
                    color: index === breadcrumb.length - 1 ? accent : "#5B6B7C",
                    "& .MuiChip-label": { px: 1 },
                  }}
                /> */}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: `${accent}1A`,
                color: accent,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: "Saira, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#1F2A37",
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Saira, sans-serif",
                  fontSize: 11,
                  color: "#6B7A8D",
                  mt: 0.25,
                }}
              >
                {countLabel}
              </Typography>
            </Box>
          </Box>
        </Box>
        {actionLabel && onAction && !isEmpty ? (
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={onAction}
            sx={{
              backgroundColor: accent,
              color: "#fff",
              textTransform: "none",
              fontFamily: "Saira, sans-serif",
              fontWeight: 700,
              fontSize: 12,
              minHeight: 40,
              px: 2.25,
              borderRadius: "8px",
              whiteSpace: "nowrap",
              boxShadow: "none",
              "& .MuiButton-startIcon": {
                marginInlineStart: 0,
                marginInlineEnd: "8px",
              },
              "&:hover": {
                backgroundColor: accent,
                opacity: 0.92,
                boxShadow: "none",
              },
            }}
          >
            {actionLabel}
          </Button>
        ) : null}
      </Box>

      {isEmpty ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
            px: 3,
            py: 4,
            textAlign: "center",
            background: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 8px,
              ${accent}08 8px,
              ${accent}08 16px
            )`,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${accent}14`,
              color: accent,
            }}
          >
            {icon}
          </Box>
          <Typography
            sx={{
              fontFamily: "Saira, sans-serif",
              fontWeight: 700,
              fontSize: 13,
              color: "#1F2A37",
            }}
          >
            {emptyTitle}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Saira, sans-serif",
              fontSize: 12,
              color: "#6B7A8D",
              maxWidth: 360,
            }}
          >
            {emptyHint}
          </Typography>
          {actionLabel && onAction ? (
            <Button
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              onClick={onAction}
              sx={{
                mt: 0.5,
                backgroundColor: accent,
                color: "#fff",
                textTransform: "none",
                fontFamily: "Saira, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                minHeight: 44,
                px: 3,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: accent,
                  opacity: 0.92,
                  boxShadow: "none",
                },
              }}
            >
              {actionLabel}
            </Button>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ p: 1 }}>{children}</Box>
      )}
    </Box>
  );
}

/** Axios cancel rejects as undefined (interceptor passes error.response). */
const isRequestCanceled = (err: any) =>
  !err ||
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED" ||
  err?.message === "New request initiated" ||
  String(err?.message || "")
    .toLowerCase()
    .includes("cancel");

const adjustActions = (
  actions: { label: string; onClick: (row: any) => void; icon?: ReactNode }[],
  editPrivilege: string,
  deletePrivilege: string,
) => {
  const canEdit = checkPrivileges(editPrivilege);
  const canDelete = checkPrivileges(deletePrivilege);
  if (canEdit && canDelete) return actions;
  if (canEdit) return actions.filter((a) => a.label !== "delete");
  if (canDelete) return actions.filter((a) => a.label !== "edit");
  return [];
};

export default function StoreRacksListing() {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const breadcrumbItems = useFrozenStoreTabBreadcrumbs("racks");
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const numericStoreId = Number(params?.id);
  const primaryColor = themeColors?.primary || "#4C0055";
  const secondaryColor = themeColors?.secondary || "#FC5F3A";

  const [loading, setLoading] = useState(true);
  const [racks, setRacks] = useState<RackResponse[]>([]);
  const [storeTitle, setStoreTitle] = useState("");
  const [expandedRacks, setExpandedRacks] = useState<Record<number, boolean>>(
    {},
  );
  const [expandedShelves, setExpandedShelves] = useState<
    Record<number, boolean>
  >({});
  const [loadingShelves, setLoadingShelves] = useState<Record<number, boolean>>(
    {},
  );
  const [loadedShelves, setLoadedShelves] = useState<Record<number, boolean>>(
    {},
  );

  const [shelfDialog, setShelfDialog] = useState<{
    open: boolean;
    rack: RackResponse | null;
    shelf: ShelfResponse | null;
  }>({ open: false, rack: null, shelf: null });
  const [binDialog, setBinDialog] = useState<{
    open: boolean;
    rack: RackResponse | null;
    shelf: ShelfResponse | null;
    bin: BinResponse | null;
  }>({ open: false, rack: null, shelf: null, bin: null });
  const [simpleForm, setSimpleForm] = useState<SimpleFormState>({
    title: "",
    code: "",
  });
  const [simpleFormErrors, setSimpleFormErrors] = useState<{
    title?: string;
    code?: string;
  }>({});
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "rack" | "shelf" | "bin";
    id: number;
    label: string;
    rackId?: number;
    shelfId?: number;
  } | null>(null);

  const showError = useCallback(
    (err: any, fallback?: string) => {
      if (isRequestCanceled(err)) return;
      enqueueSnackbar(
        <AlertMessages
          text={
            err?.message ||
            err?.data?.message ||
            fallback ||
            "Something went wrong"
          }
        />,
        { variant: "error" },
      );
    },
    [enqueueSnackbar],
  );

  const loadStoreMeta = useCallback(() => {
    if (!numericStoreId) return;
    StoreProfileService.getStoreByID(numericStoreId)
      .then((res) => {
        if (res?.success && res.data) {
          if (!isBinInventoryMode(res.data.inventoryMode)) {
            navigate(`/store/${numericStoreId}`, { replace: true });
            return;
          }
          setStoreTitle(res.data.title || "");
        }
      })
      .catch((err) => {
        if (isRequestCanceled(err)) return;
      });
  }, [numericStoreId, navigate]);

  const loadRacks = useCallback(() => {
    if (!numericStoreId) return;
    setLoading(true);
    RackProfileService.getRacks({
      storeId: numericStoreId,
      page: 1,
      size: 100,
    })
      .then((res) => {
        if (res?.success) {
          setRacks(normalizeRacksList(res).map((r) => ({ ...r, shelves: [] })));
          setLoadedShelves({});
          setExpandedShelves({});
        } else {
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => showError(err))
      .finally(() => setLoading(false));
  }, [numericStoreId, enqueueSnackbar, showError]);

  const loadShelvesForRack = useCallback(
    (rackId: number, force = false) => {
      if (!force && loadedShelves[rackId]) return;
      setLoadingShelves((prev) => ({ ...prev, [rackId]: true }));
      RackProfileService.getRackById(rackId)
        .then((res) => {
          if (res?.success) {
            const shelves = normalizeShelvesFromRack(res);
            setRacks((prev) =>
              prev.map((r) => {
                if (r.id !== rackId) return r;
                return {
                  ...r,
                  code: res.data?.code ?? r.code,
                  title: res.data?.title ?? r.title,
                  shelves,
                  shelfCount: shelves.length,
                  binCount: shelves.reduce(
                    (sum, s) => sum + (s.bins?.length || 0),
                    0,
                  ),
                };
              }),
            );
            setLoadedShelves((prev) => ({ ...prev, [rackId]: true }));
          } else {
            enqueueSnackbar(
              <AlertMessages text={res?.message || "Something went wrong"} />,
              { variant: "error" },
            );
          }
        })
        .catch((err) => showError(err))
        .finally(() =>
          setLoadingShelves((prev) => ({ ...prev, [rackId]: false })),
        );
    },
    [loadedShelves, enqueueSnackbar, showError],
  );

  useEffect(() => {
    loadStoreMeta();
    loadRacks();
  }, [loadStoreMeta, loadRacks]);

  const toggleRack = (row: RackResponse) => {
    const rackId = row.id;
    setExpandedRacks((prev) => {
      const nextOpen = !prev[rackId];
      if (nextOpen) loadShelvesForRack(rackId);
      return { ...prev, [rackId]: nextOpen };
    });
  };

  const toggleShelf = (row: ShelfResponse) => {
    setExpandedShelves((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
  };

  const openShelfForm = (rack: RackResponse, shelf?: ShelfResponse | null) => {
    const nextIndex = (rack.shelves?.length || 0) + 1;
    setSimpleFormErrors({});
    setSimpleForm({
      title: shelf?.title || `Shelf ${nextIndex}`,
      code: shelf?.code || `S${nextIndex}`,
    });
    setShelfDialog({ open: true, rack, shelf: shelf || null });
  };

  const saveShelf = () => {
    if (!shelfDialog.rack || !numericStoreId) return;
    const title = simpleForm.title.trim();
    const code = simpleForm.code.trim();
    const errors: { title?: string; code?: string } = {};
    if (!code) errors.code = t("shelfCodeRequired");
    if (Object.keys(errors).length) {
      setSimpleFormErrors(errors);
      enqueueSnackbar(
        <AlertMessages text={errors.code || t("shelfCodeRequired")} />,
        { variant: "error" },
      );
      return;
    }
    setSimpleFormErrors({});
    const rackId = shelfDialog.rack.id;
    const body = {
      title,
      code,
      storeId: numericStoreId,
      rackId,
    };
    const req = shelfDialog.shelf
      ? RackProfileService.updateShelf(shelfDialog.shelf.id, body)
      : RackProfileService.createShelf(body);
    req
      .then((res: any) => {
        if (res?.success) {
          setShelfDialog({ open: false, rack: null, shelf: null });
          setExpandedRacks((prev) => ({ ...prev, [rackId]: true }));
          loadShelvesForRack(rackId, true);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => showError(err));
  };

  const openBinForm = (
    rack: RackResponse,
    shelf: ShelfResponse,
    bin?: BinResponse | null,
  ) => {
    const nextIndex = (shelf.bins?.length || 0) + 1;
    setSimpleFormErrors({});
    setSimpleForm({
      title: bin?.title || `Bin ${nextIndex}`,
      code: bin?.code || `B${nextIndex}`,
    });
    setBinDialog({ open: true, rack, shelf, bin: bin || null });
  };

  const saveBin = () => {
    if (!binDialog.rack || !binDialog.shelf || !numericStoreId) return;
    const title = simpleForm.title.trim();
    const code = simpleForm.code.trim();
    if (!code) {
      setSimpleFormErrors({ code: t("binCodeRequired") });
      enqueueSnackbar(<AlertMessages text={t("binCodeRequired")} />, {
        variant: "error",
      });
      return;
    }
    setSimpleFormErrors({});
    const rackId = binDialog.rack.id;
    const shelfId = binDialog.shelf.id;
    const body = {
      title,
      code,
      storeId: numericStoreId,
      rackId,
      shelfId,
    };
    const req = binDialog.bin
      ? RackProfileService.updateBin(binDialog.bin.id, body)
      : RackProfileService.createBin(body);
    req
      .then((res: any) => {
        if (res?.success) {
          setBinDialog({ open: false, rack: null, shelf: null, bin: null });
          setExpandedRacks((prev) => ({ ...prev, [rackId]: true }));
          setExpandedShelves((prev) => ({ ...prev, [shelfId]: true }));
          loadShelvesForRack(rackId, true);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => showError(err));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const { type, id, rackId } = deleteTarget;
    const map = {
      rack: () => RackProfileService.deleteRack(id),
      shelf: () => RackProfileService.deleteShelf(id),
      bin: () => RackProfileService.deleteBin(id),
    };
    map[type]()
      .then((res: any) => {
        if (res?.success) {
          setDeleteTarget(null);
          if (type === "rack") {
            loadRacks();
          } else if ((type === "shelf" || type === "bin") && rackId) {
            loadShelvesForRack(rackId, true);
          } else {
            loadRacks();
          }
        } else {
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => showError(err));
  };

  const rackActions = useMemo(
    () =>
      adjustActions(
        [
          {
            label: "edit",
            icon: <EditIcon />,
            onClick: (row: RackResponse) =>
              navigate(`/store/${numericStoreId}/rack/${row.id}`),
          },
          {
            label: "delete",
            icon: <DeleteIcon />,
            onClick: (row: RackResponse) =>
              setDeleteTarget({
                type: "rack",
                id: row.id,
                label: getRackTitle(row),
              }),
          },
        ],
        "ROLE_RACK_UPDATE",
        "ROLE_RACK_ARCHIVE",
      ),
    [navigate, numericStoreId],
  );

  const rackRows = useMemo(
    () =>
      racks.map((rack) => {
        const shelves = rack.shelves || [];
        const loaded = !!loadedShelves[rack.id];
        const shelfCount = loaded
          ? shelves.length
          : Number(rack.shelfCount ?? 0);
        const binCount = loaded
          ? countRackBins(rack)
          : Number(rack.binCount ?? 0);
        return {
          ...rack,
          title: getRackTitle(rack),
          code: getRackCode(rack),
          shelfCount: String(shelfCount),
          binCount: String(binCount),
        };
      }),
    [racks, loadedShelves],
  );

  const renderBinsTable = (rack: RackResponse, shelf: ShelfResponse) => {
    const rackLabel = getRackTitle(rack) || getRackCode(rack) || t("racks");
    const shelfLabel = shelf.title || shelf.code || t("shelves");
    const bins = (shelf.bins || []).map((bin) => ({
      ...bin,
      locationCode:
        bin.locationCode || fullBinAddress(rack, shelf.code, bin.code),
      stockQuantityDisplay:
        bin.stockQuantity != null ? String(bin.stockQuantity) : "",
    }));
    const canAddBin = checkPrivileges("ROLE_BIN_CREATE");
    const binActions = adjustActions(
      [
        {
          label: "edit",
          icon: <EditIcon />,
          onClick: (row: BinResponse) => openBinForm(rack, shelf, row),
        },
        {
          label: "delete",
          icon: <DeleteIcon />,
          onClick: (row: BinResponse) =>
            setDeleteTarget({
              type: "bin",
              id: row.id,
              label: row.code,
              rackId: rack.id,
              shelfId: shelf.id,
            }),
        },
      ],
      "ROLE_BIN_UPDATE",
      "ROLE_BIN_ARCHIVE",
    );

    return (
      <NestedSection
        accent={secondaryColor}
        icon={<Inventory2OutlinedIcon fontSize="small" />}
        title={t("binsInShelf", { shelf: shelfLabel })}
        breadcrumb={[rackLabel, shelfLabel, t("bins")]}
        countLabel={t("binsCountLabel", { count: bins.length })}
        emptyTitle={t("noBinsInShelf")}
        emptyHint={t("noBinsInShelfHint", { shelf: shelfLabel })}
        isEmpty={bins.length === 0}
        actionLabel={
          canAddBin ? t("addBinToShelf", { shelf: shelfLabel }) : undefined
        }
        onAction={canAddBin ? () => openBinForm(rack, shelf) : undefined}
      >
        <BackendPaginationTable
          columns={[
            { id: "code", label: t("binCode"), numeric: false },
            { id: "title", label: t("binName"), numeric: false },
            {
              id: "locationCode",
              label: t("location"),
              numeric: false,
            },
            ...(binActions.length
              ? [{ id: "actions", label: t("actions"), numeric: false }]
              : []),
          ]}
          data={bins}
          pageNo={1}
          pageSize={Math.max(bins.length, 1)}
          tableData={{ content: bins, totalElements: bins.length }}
          showCheckbox={false}
          showActions={true}
          enableSorting={false}
          showHeader={true}
          showSearch={false}
          showFilter={false}
          showCreateButton={false}
          actions={binActions}
          handleChangePage={() => {}}
          handleChangeRows={() => {}}
          rowClick={false}
          loadingSearch={false}
          hidePagination
        />
      </NestedSection>
    );
  };

  const renderShelvesTable = (rack: RackResponse) => {
    const rackLabel = getRackTitle(rack) || getRackCode(rack) || t("racks");
    const shelves = (rack.shelves || []).map((shelf) => ({
      ...shelf,
      binCount: String(shelf.bins?.length || 0),
    }));
    const canAddShelf = checkPrivileges("ROLE_SHELF_CREATE");
    const shelfActions = adjustActions(
      [
        {
          label: "edit",
          icon: <EditIcon />,
          onClick: (row: ShelfResponse) => openShelfForm(rack, row),
        },
        {
          label: "delete",
          icon: <DeleteIcon />,
          onClick: (row: ShelfResponse) =>
            setDeleteTarget({
              type: "shelf",
              id: row.id,
              label: row.title,
              rackId: rack.id,
            }),
        },
      ],
      "ROLE_SHELF_UPDATE",
      "ROLE_SHELF_ARCHIVE",
    );

    return (
      <NestedSection
        accent={primaryColor}
        icon={<ViewModuleOutlinedIcon fontSize="small" />}
        title={t("shelvesInRack", { rack: rackLabel })}
        breadcrumb={[rackLabel, t("shelves")]}
        countLabel={t("shelvesCountLabel", { count: shelves.length })}
        emptyTitle={t("noShelvesInRack")}
        emptyHint={t("noShelvesInRackHint", { rack: rackLabel })}
        isEmpty={shelves.length === 0}
        actionLabel={
          canAddShelf ? t("addShelfToRack", { rack: rackLabel }) : undefined
        }
        onAction={canAddShelf ? () => openShelfForm(rack) : undefined}
      >
        <BackendPaginationTable
          columns={[
            { id: "title", label: t("shelfName"), numeric: false },
            { id: "code", label: t("shelfCode"), numeric: false },
            { id: "binCount", label: t("bins"), numeric: false },
            ...(shelfActions.length
              ? [{ id: "actions", label: t("actions"), numeric: false }]
              : []),
          ]}
          data={shelves}
          pageNo={1}
          pageSize={Math.max(shelves.length, 1)}
          tableData={{ content: shelves, totalElements: shelves.length }}
          showCheckbox={false}
          showActions={true}
          enableSorting={false}
          showHeader={true}
          showSearch={false}
          showFilter={false}
          showCreateButton={false}
          actions={shelfActions}
          handleChangePage={() => {}}
          handleChangeRows={() => {}}
          rowClick={false}
          loadingSearch={false}
          hidePagination
          expandable
          expandedRowIds={expandedShelves}
          onToggleExpand={toggleShelf}
          renderExpandedContent={(shelfRow: ShelfResponse) => {
            const shelf =
              (rack.shelves || []).find((s) => s.id === shelfRow.id) ||
              shelfRow;
            return renderBinsTable(rack, shelf);
          }}
        />
      </NestedSection>
    );
  };

  return (
    <Box>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate(`/store/${numericStoreId}/rack/create`)}
        heading={storeTitle || t("racks")}
        icon={<StoreIcon height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_RACK_CREATE")}
      />
      <Box className="flex">
        <StoreTabs params={params} selectedIndex={1} showRacks />
        <Box className="mt-[10px] w-[100%]">
          {loading ? (
            <Loader />
          ) : (
            <BackendPaginationTable
              columns={[
                { id: "title", label: t("rackName"), numeric: false },
                { id: "code", label: t("rackCode"), numeric: false },
                { id: "shelfCount", label: t("shelves"), numeric: false },
                { id: "binCount", label: t("bins"), numeric: false },
                ...(rackActions.length
                  ? [{ id: "actions", label: t("actions"), numeric: false }]
                  : []),
              ]}
              data={rackRows}
              pageNo={1}
              pageSize={Math.max(rackRows.length, 1)}
              tableData={{
                content: rackRows,
                totalElements: rackRows.length,
              }}
              showCheckbox={false}
              showActions={true}
              enableSorting={true}
              showHeader={true}
              showSearch={false}
              showFilter={false}
              showCreateButton={false}
              actions={rackActions}
              handleChangePage={() => {}}
              handleChangeRows={() => {}}
              rowClick={false}
              loadingSearch={false}
              hidePagination
              expandable
              expandedRowIds={expandedRacks}
              onToggleExpand={toggleRack}
              expandLoadingIds={loadingShelves}
              renderExpandedContent={(row: RackResponse) => {
                const rack = racks.find((r) => r.id === row.id) || row;
                return renderShelvesTable(rack);
              }}
            />
          )}
        </Box>
      </Box>

      <FormDialog
        open={shelfDialog.open}
        onClose={() => {
          setShelfDialog({ open: false, rack: null, shelf: null });
          setSimpleFormErrors({});
        }}
        title={shelfDialog.shelf ? t("editShelf") : t("createShelf")}
        submitButtonText={shelfDialog.shelf ? t("update") : t("create")}
        saveFunction={saveShelf}
        maxWidth="sm"
        fullWidth
        content={
          <Grid container spacing={2} className="pt-2">
            <Grid item xs={12} sm={6}>
              <Typography className="font-Saira text-[11px] font-semibold text-[#6B7A8D] mb-1 uppercase">
                {t("shelfName")}
              </Typography>
              <input
                className="w-full border border-[#E3E8EE] rounded-md px-3 py-2 font-Saira text-[13px]"
                value={simpleForm.title}
                onChange={(e) =>
                  setSimpleForm((p) => ({ ...p, title: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className="font-Saira text-[11px] font-semibold text-[#6B7A8D] mb-1 uppercase">
                {t("shelfCode")} *
              </Typography>
              <input
                className={`w-full border rounded-md px-3 py-2 font-Saira text-[13px] ${
                  simpleFormErrors.code
                    ? "border-[#D32F2F]"
                    : "border-[#E3E8EE]"
                }`}
                value={simpleForm.code}
                onChange={(e) => {
                  setSimpleForm((p) => ({ ...p, code: e.target.value }));
                  if (simpleFormErrors.code) {
                    setSimpleFormErrors((prev) => ({
                      ...prev,
                      code: undefined,
                    }));
                  }
                }}
              />
              {simpleFormErrors.code ? (
                <Typography className="font-Saira text-[11px] text-[#D32F2F] mt-1">
                  {simpleFormErrors.code}
                </Typography>
              ) : null}
            </Grid>
          </Grid>
        }
      />

      <FormDialog
        open={binDialog.open}
        onClose={() => {
          setBinDialog({ open: false, rack: null, shelf: null, bin: null });
          setSimpleFormErrors({});
        }}
        title={binDialog.bin ? t("editBin") : t("createBin")}
        submitButtonText={binDialog.bin ? t("update") : t("create")}
        saveFunction={saveBin}
        maxWidth="sm"
        fullWidth
        content={
          <Grid container spacing={2} className="pt-2">
            <Grid item xs={12} sm={6}>
              <Typography className="font-Saira text-[11px] font-semibold text-[#6B7A8D] mb-1 uppercase">
                {t("binName")}
              </Typography>
              <input
                className="w-full border border-[#E3E8EE] rounded-md px-3 py-2 font-Saira text-[13px]"
                value={simpleForm.title}
                onChange={(e) =>
                  setSimpleForm((p) => ({ ...p, title: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className="font-Saira text-[11px] font-semibold text-[#6B7A8D] mb-1 uppercase">
                {t("binCode")} *
              </Typography>
              <input
                className={`w-full border rounded-md px-3 py-2 font-Saira text-[13px] ${
                  simpleFormErrors.code
                    ? "border-[#D32F2F]"
                    : "border-[#E3E8EE]"
                }`}
                value={simpleForm.code}
                onChange={(e) => {
                  setSimpleForm((p) => ({ ...p, code: e.target.value }));
                  if (simpleFormErrors.code) {
                    setSimpleFormErrors((prev) => ({
                      ...prev,
                      code: undefined,
                    }));
                  }
                }}
              />
              {simpleFormErrors.code ? (
                <Typography className="font-Saira text-[11px] text-[#D32F2F] mt-1">
                  {simpleFormErrors.code}
                </Typography>
              ) : null}
            </Grid>
            {binDialog.rack && binDialog.shelf ? (
              <Grid item xs={12}>
                <Box className="border border-dashed border-[#C9A9C6] bg-[#F1E9F0] rounded-md px-3 py-2 font-mono text-[#4A0A46] font-semibold">
                  {fullBinAddress(
                    binDialog.rack,
                    binDialog.shelf.code,
                    simpleForm.code || "B?",
                  )}
                </Box>
              </Grid>
            ) : null}
          </Grid>
        }
      />

      {deleteTarget ? (
        <FormDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title={t("delete")}
          submitButtonText={t("delete")}
          saveFunction={confirmDelete}
          content={
            <Typography className="font-Saira">
              {t("Are you sure you want to delete?")} {deleteTarget.label}
            </Typography>
          }
        />
      ) : null}
    </Box>
  );
}
